/** @jsxImportSource @emotion/react */
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import "react-tabulator/css/bootstrap/tabulator_bootstrap.min.css";
import "react-tabulator/lib/styles.css";
import tw from "twin.macro";
import { LayoutProps } from "./Layout.d";
import SideNav from "./SideNav";

import { Button } from "components/Button";
import { Modal } from "components/Modal";
import { UserContext } from "context/UserContext";
import { useContext, useEffect, useState } from "react";
import { AuthService } from "services/Auth";
import UserService from "services/User";
import { addEventListeners, removeEventListeners } from "utils/helpers";

import NotificationContainer from "components/Notification/AlertContainer";
import { NotificationProvider } from "context/NotificationContext";

const Layout: React.FC<LayoutProps> = () => {
  const userService = new UserService();
  const authService = new AuthService();
  const { setIsLoggedIn, signout, windowWidth } = useContext(UserContext);
  const [warningModalOpen, setWarningModalOpen] = useState<boolean | undefined>(
    undefined
  );
  const [expireModalOpen, setExpiredModalOpen] = useState<boolean | undefined>(
    undefined
  );
  const currentUser = userService.getUser();
  const warningTimer = 60 * 1000 * 55; // 55 minutes
  const expireTimer = 60 * 1000 * 5; // 5 minutes (after warning)
  const navigator = useNavigate();

  useEffect(() => {
    const sessionWarningTimeout = () =>
      setTimeout(() => {
        setWarningModalOpen(true);
        timeout = sessionExpiredTimeout();
      }, warningTimer);

    const sessionExpiredTimeout = () =>
      setTimeout(() => {
        signout();
        setIsLoggedIn(false);

        setWarningModalOpen(undefined);
        setExpiredModalOpen(true);
        clearTimeout(timeout);
      }, expireTimer);

    const listener = () => {
      if (!warningModalOpen) {
        clearTimeout(timeout);
        timeout = sessionWarningTimeout();
      }
    };
    let timeout = warningModalOpen
      ? sessionExpiredTimeout()
      : sessionWarningTimeout();

    const refreshUser = () => {
      authService
        .validate_session()
        .then((resp) => {
          if (!resp) {
            signout();
          }
        })
        .catch((err) => {
          signout();
        });
    };
    // Refresh user every 60 seconds if not in warning modal
    const refreshInterval = setInterval(() => {
      if (!warningModalOpen && !expireModalOpen) refreshUser();
    }, 60000);

    addEventListeners(listener);
    refreshUser();
    return () => {
      removeEventListeners(listener);
      clearTimeout(timeout);
      clearInterval(refreshInterval);
    };
  }, [warningModalOpen, expireModalOpen, setIsLoggedIn, authService]);

  const handleLogout = () => {
    setWarningModalOpen(undefined);
    setExpiredModalOpen(undefined);
    signout();
  };

  return (
    <NotificationProvider>
      <>
        <NotificationContainer />
        {currentUser ? (
          <div css={[tw`grid grid-cols-12`]}>
            <div
              css={[
                tw`block`,
                windowWidth > 1920 ? tw`lg:col-span-1` : tw`lg:col-span-2`,
              ]}
            >
              <SideNav />
            </div>
            <div
              css={[
                tw`col-span-12`,
                windowWidth > 1920 ? tw`lg:col-span-11` : tw`lg:col-span-10`,
              ]}
            >
              <Outlet />
            </div>
          </div>
        ) : (
          <Navigate to="/login" replace />
        )}

        <Modal
          title="Inactive Session"
          isOpen={warningModalOpen}
          width="s"
          kind="danger"
          onClose={async () => {
            const r = await authService.validate_session();
            if (r) {
              setWarningModalOpen(undefined);
            } else {
              setExpiredModalOpen(true);
            }
          }}
          disableCloseButton={true}
          outsideCloseDisabled={true}
          content={
            <div css={[tw`flex gap-x-4 items-center`]}>
              <ExclamationTriangleIcon css={[tw`text-red-500 w-10`]} />
              You will be logged out due to inactivity.
            </div>
          }
          primary={
            <Button
              onClick={async () => {
                const r = await authService.validate_session();
                if (r) {
                  setWarningModalOpen(undefined);
                } else {
                  setExpiredModalOpen(true);
                }
              }}
            >
              Continue
            </Button>
          }
          secondary={
            <Button variant="outline-fill" kind="danger" onClick={signout}>
              Log Out
            </Button>
          }
        />
        <Modal
          title="Expired Session"
          isOpen={expireModalOpen}
          kind="danger"
          width="s"
          disableCloseButton={true}
          outsideCloseDisabled={true}
          onClose={() => setWarningModalOpen(undefined)}
          content={
            <div css={[tw`flex gap-x-4 items-center`]}>
              <ExclamationCircleIcon css={[tw`text-red-500 w-10`]} />
              You're session has expired. Please log in again.
            </div>
          }
          primary={<Button onClick={handleLogout}>Login</Button>}
        />
      </>
    </NotificationProvider>
  );
};

export default Layout;
